/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */
import React, { useContext, useEffect, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ThemeContext } from "../../contexts/ThemeContext";
import ErrorBoundary from "../general/ErrorBoundary";
import GeneralNavbar from "../general/GeneralNavbar";
import PsychometricTestNavbar from '../psychometric/PyschometricTestNavbar';
import PsychometricService from "../../services/PsychometricService";

import PsychometricTestPDFLayout from "./PsychometricTestPDFLayout";
import ReactDOMServer from "react-dom/server";

import html2pdf from 'html2pdf.js';
const PsychometricTestSkillsLikert = ({   
  goToNextStep, 
  goToPreviousStep, 
  history, 
  location,
  isOrganization,
  signedID,
  signedFirstName,
  signedLastName,
  signedOrganization,
  signedEmail,
  ...props  }) => {

  const { theme, fontFamily, colors, fontWeight } = useContext(ThemeContext);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [userData, setUserData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const isIframe = new URLSearchParams(location.search).get('iframe') === 'true';
  const likertOptions = [
    "Not True",
    "Seldom True",
    "Occasionally True",
    "Somewhat True",
    "Very True",
  ];
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {

    // Validate required parameters based on user type
    if (isOrganization === true) {
      if (!signedOrganization || !signedFirstName || !signedLastName || !signedID) {
        console.log("Redirecting due to missing organization parameters...");
        history.replace("/psychometricSignIn");
        return;
      }
    } else {
      if (!signedEmail) {
        console.log("Redirecting due to missing email...");
        history.replace("/psychometricSignIn");
        return;
      }
    }
    const fetchQuestions = async () => {
      try {
        const response = await PsychometricService.getPsychometricTest("english");
     
        const section = response.test.sections.find((section) => section.number === "3");
        setQuestions(Object.values(section.properties));
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading questions:", error);
      }
    };

    fetchQuestions();
  }, [ signedOrganization, signedEmail, isOrganization,  signedID, signedFirstName,signedLastName, history]);

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionIndex]: value,
    }));
  };

  const updateAnswer = async (questionIndex, value) => {
    const answerValue = likertOptions.indexOf(value) + 1;
    try {


      if(isOrganization){
        await PsychometricService.updateSkillsAnswers(
          signedID,
          signedFirstName,
          signedLastName,
          signedOrganization,
          questionIndex,
          answerValue
        );

      }else{
        await PsychometricService.updateIndividualSkillsAnswers(
          signedEmail,
          questionIndex,
          answerValue
        );

      }

    } catch (error) {
      console.error("Error updating answer:", error);
    }
  };

  const handleNextQuestion = async () => {
    if (!answers[currentQuestionIndex]) {
      setModalOpen(true);
      return;
    }
    await updateAnswer(currentQuestionIndex, answers[currentQuestionIndex]);
    setCurrentQuestionIndex((prevIndex) =>
      Math.min(prevIndex + 1, questions.length - 1)
    );
  };

  const handlePreviousQuestion = async () => {
    if (answers[currentQuestionIndex]) {
      await updateAnswer(currentQuestionIndex, answers[currentQuestionIndex]);
    }
    setCurrentQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };



  const handleSubmit = async () => {
  
    if (!answers[currentQuestionIndex]) {
      setModalOpen(true);
      return;
    }
    try {
      await updateAnswer(currentQuestionIndex, answers[currentQuestionIndex]);
      setSubmitted(true);
      let userData;
      if(isOrganization){
       userData = await PsychometricService.getPsychometricUser(
        signedID,
        signedFirstName,
        signedLastName,
        signedOrganization
      );
    }else{
       
      userData = await PsychometricService.getPsychometricIndividual(
        signedEmail
      );
    }





    if(isOrganization){
      await Promise.all([
        PsychometricService.updateSkillsScores(
          signedID,
          signedFirstName,
          signedLastName,
          signedOrganization
        ),
        PsychometricService.updateInterestsScores(
          signedID,
          signedFirstName,
          signedLastName,
          signedOrganization
        ),
        PsychometricService.updateTraitsScores(
          signedID,
          signedFirstName,
          signedLastName,
          signedOrganization
        ),
      ]);

      const tempUserData = await PsychometricService.getPsychometricUser(
        signedID,
        signedFirstName,
        signedLastName,
        signedOrganization
      );

      setUserData(tempUserData);
      generatePDF(tempUserData);

      goToNextStep();



    }else{

      await Promise.all([
        PsychometricService.updateIndividualSkillsScores(
            signedEmail
        ),
        PsychometricService.updateIndividualInterestsScores(
          signedEmail
        ),
        PsychometricService.updateIndividualTraitsScores(
          signedEmail
        ),
      ]);

      const tempUserData = await PsychometricService.getPsychometricIndividual(
          signedEmail
      );
      setUserData(tempUserData);
      generatePDF(tempUserData);

      goToNextStep();

    }

      
    } catch (error) {
      console.error("Error updating scores:", error);
    }
    goToNextStep();
  };

  const generatePDF = async (userData) => {
    setIsGeneratingPDF(true);
    
  
    
    const element = document.createElement('div');
    element.innerHTML = ReactDOMServer.renderToString(
      <PsychometricTestPDFLayout userData={userData.user} />
    );
    document.body.appendChild(element);

    const opt = {
      margin: 10,
      filename: `psychometric-report-${userData.user.firstName}-${userData.user.lastName}.pdf`,
      image: { 
        type: 'jpeg', 
        quality: 0.8  // Reduced from 0.98 to 0.8 for smaller file size
      },
      html2canvas: { 
        scale: 1.5,  // Reduced from 2 to 1.5 to decrease file size while maintaining quality
        useCORS: true,
        logging: true
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait',
        compress: true  // Enable PDF compression
      }
    };

    try {
      const pdf = await html2pdf().set(opt).from(element).output('blob');
      
      // Convert blob to base64 using Promise
      const base64data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          // Get only the base64 data part, removing the data URL prefix
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdf);
      });

      try {
        if(isOrganization) {
          const {organizationEmail} = await PsychometricService.getUserOrganizationEmail(
            signedID, 
            signedFirstName,
            signedLastName,
            signedOrganization
          );
          
          await PsychometricService.sendReport(
            signedID,
            signedFirstName,
            signedLastName,
            signedOrganization,
            organizationEmail,
            base64data
          );
        } else {
          await PsychometricService.sendIndividualReport(
            signedEmail,
            base64data
          );
        }
        console.log("Email sent successfully");
      } catch (error) {
        console.error("Error sending email:", error);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsGeneratingPDF(false);
      document.body.removeChild(element);
    }
  };

  const confirmSubmission = () => {
    setSubmitModalOpen(false);
    handleSubmit();
  };

  const minProgressPercentage = 80;
  const maxProgressPercentage = 100;
  const totalQuestions = questions.length;
  const progressPercentage =
    minProgressPercentage +
    (currentQuestionIndex / (totalQuestions - 1)) *
      (maxProgressPercentage - minProgressPercentage);

  return (
    <div style={{ fontFamily: fontFamily.primary, color: colors.text }}>
            {isIframe && (
                <ErrorBoundary errorAt="At PsychometricSkillslikert PsychometricTestNavbar">
                    <PsychometricTestNavbar signedOrganization={signedOrganization} />
                </ErrorBoundary>
            )}
            {!isIframe && (
                <ErrorBoundary errorAt="At General PsychometricSkillsLikert GeneralNavbar">
                     <PsychometricTestNavbar signedOrganization={signedOrganization} />
                </ErrorBoundary>
            )}
      <div className="progress" style={{ height: "10px", marginBottom: "10px" }}>
        <div
          className="progress-bar bg-success"
          role="progressbar"
          style={{
            width: `${progressPercentage}%`,
            background: "linear-gradient(to right, #28a745 20%, transparent 20%)",
          }}
          aria-valuenow={progressPercentage}
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
      <div className="row p-3 px-5">
        <div
          className="col-md-12 col-12 mb-4"
          style={{
            textAlign: "center",
            fontSize: "1.5rem",
            fontWeight: fontWeight.bold,
            color: colors.primaryGreenDark,
          }}
        >
          Psychometric Test: Your Skills
        </div>
        {isLoading ? (
          <div className="col-md-12 col-12 mb-4" style={{ textAlign: "center" }}>
            Loading...
          </div>
        ) : (
          <div className="col-md-12 col-12 mb-4" style={{ textAlign: "left" }}>
            <div className="mb-4" style={{ fontSize: "1.2rem" }}>
              <p>
                <strong>
                  {currentQuestionIndex + 1} / {questions.length}
                </strong>
                : {questions[currentQuestionIndex]}
              </p>
              <div
                className="likert-options"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  marginLeft: "15px",
                }}
              >
                {likertOptions.map((option, index) => (
                  <label
                    key={index}
                    style={{
                      margin: "5px 0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestionIndex}`}
                      value={option}
                      checked={answers[currentQuestionIndex] === option}
                      onChange={() => handleAnswerChange(currentQuestionIndex, option)}
                    />
                    <span style={{ marginLeft: "5px", fontWeight: "normal" }}>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <Button
              id="previousButton"
                type="button"
                color="secondary"
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0}
                style={theme.newButtonRounded}
              >
                Back
              </Button>
              <Button
               id="nextButton"
                type="button"
                color="secondary"
                onClick={handleNextQuestion}
                disabled={currentQuestionIndex === questions.length - 1}
                style={theme.newButtonRounded}
              >
                Next
              </Button>
            </div>

            {currentQuestionIndex === questions.length - 1 && (
              <div className="mt-4 d-flex justify-content-between">
                <Button
                  id="submitSkillsButton"
                  type="button"
                  color="primary"
                  onClick={() => setSubmitModalOpen(true)}
                  style={theme.formSubmit}
                  disabled={isGeneratingPDF}
                >
                  {isGeneratingPDF ? "Generating PDF..." : "Submit"}
                </Button>
              </div>
            )}
          </div>
        )}
            <footer style={theme.psychometricTestFooter}>
                Powered by Umang Wellness
            </footer>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>Incomplete Answer</ModalHeader>
        <ModalBody>Please select an answer before proceeding to the next question</ModalBody>
        <ModalFooter>
          <Button color="secondary" id="modalclose" onClick={() => setModalOpen(false)} style={theme.formSubmit}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={submitModalOpen} toggle={() => setSubmitModalOpen(false)}>
        <ModalHeader toggle={() => setSubmitModalOpen(false)}>Confirm Submission</ModalHeader>
        <ModalBody>Are you sure you want to submit your answers? You won't be able to change them later.</ModalBody>
        <ModalFooter>
          <Button color="secondary" id="modalcancel" onClick={() => setSubmitModalOpen(false)} style={theme.formSubmit}>
            Cancel
          </Button>
          <Button color="primary" id="modalconfirm"onClick={confirmSubmission} style={theme.formSubmit}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PsychometricTestSkillsLikert;
